/**
 * Member-level permission overrides.
 *
 * Storage: `clinic_members.permissions_override` (jsonb<string[]>). Already
 * on the schema since day 0 but never used until now.
 *
 * Model:
 *   effective = (role_defaults ∪ grants) − denies
 *
 * Grants/denies are encoded with a `+`/`-` prefix in the JSONB array,
 * so a single column carries both:
 *   ['+commission.view', '-billing.manage']
 *
 * The override is *editable for non-owner members only*. Owner always
 * has every permission — that invariant is enforced server-side so a
 * bug in the UI can never lock the owner out of their own clinic.
 */
import { z } from "zod";
import type { ClinicRole } from "./staff.ts";

// ===== Catalog =====
// New permission keys go here AND in DEFAULT_PERMISSIONS_BY_ROLE below.
// Pattern: <domain>.<view|manage>. Keep keys terse — they show up in
// JWT-adjacent paths and tooltips.

export const PERMISSIONS = [
	"patients.view",
	"patients.manage",
	"appointments.view",
	"appointments.manage",
	"clinical.view", // visit notes, prescriptions, lab orders, vitals, documents
	"clinical.manage",
	"billing.view",
	"billing.manage",
	"commission.view",
	"audit.view",
	"analytics.view",
	"schedule.view",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const permissionSchema = z.enum(PERMISSIONS);

// ===== Role defaults =====
// Owner is intentionally absent — we resolve "owner → every permission"
// at lookup time so adding a new permission grants it to the owner
// automatically (no migration to backfill).
export const DEFAULT_PERMISSIONS_BY_ROLE: Record<
	Exclude<ClinicRole, "owner">,
	ReadonlyArray<Permission>
> = {
	doctor: [
		"patients.view",
		"patients.manage",
		"appointments.view",
		"appointments.manage",
		"clinical.view",
		"clinical.manage",
		"schedule.view",
		"analytics.view",
	],
	receptionist: [
		"patients.view",
		"patients.manage",
		"appointments.view",
		"appointments.manage",
		"billing.view",
		"billing.manage",
		"schedule.view",
	],
	nurse: [
		"patients.view",
		"appointments.view",
		"clinical.view",
		"clinical.manage",
		"schedule.view",
	],
	accountant: [
		"patients.view",
		"billing.view",
		"billing.manage",
		"commission.view",
		"analytics.view",
	],
	// Lab technician — registers walk-in patients, runs the lab worklist
	// (lab orders + results live under clinical.*), and takes payment.
	lab_tech: [
		"patients.view",
		"patients.manage",
		"clinical.view",
		"clinical.manage",
		"billing.view",
		"billing.manage",
	],
};

// ===== Override encoding =====
// One JSONB array carries both grants and denies. The +/- prefix is part
// of the stored value — never strip it client-side except when
// presenting in a UI.

const overrideEntrySchema = z
	.string()
	.regex(
		/^[+-](patients|appointments|clinical|billing|commission|audit|analytics|schedule)\.(view|manage)$/u,
		'Override must be +permission or -permission, e.g. "+commission.view"',
	);

export const permissionsOverrideSchema = z.array(overrideEntrySchema).max(64);
export type PermissionsOverride = z.infer<typeof permissionsOverrideSchema>;

/**
 * Split overrides into grants and denies as plain Permission arrays.
 * Unknown keys are silently skipped — the validator above prevents them
 * server-side, but this stays defensive for older stored data.
 */
export function parseOverrides(
	override: ReadonlyArray<string> | null | undefined,
): {
	grants: Permission[];
	denies: Permission[];
} {
	const grants: Permission[] = [];
	const denies: Permission[] = [];
	if (!override) return { grants, denies };
	const known = new Set<string>(PERMISSIONS);
	for (const raw of override) {
		if (raw.length < 2) continue;
		const sign = raw[0];
		const key = raw.slice(1);
		if (!known.has(key)) continue;
		if (sign === "+") grants.push(key as Permission);
		else if (sign === "-") denies.push(key as Permission);
	}
	return { grants, denies };
}

/**
 * Resolve the effective permission set for a member.
 *
 * - Owner always gets every permission. Overrides are ignored for them.
 * - Other roles: defaults ∪ grants − denies.
 */
export function resolveEffectivePermissions(
	role: ClinicRole,
	override: ReadonlyArray<string> | null | undefined,
): Permission[] {
	if (role === "owner") return [...PERMISSIONS];
	const defaults = DEFAULT_PERMISSIONS_BY_ROLE[role] ?? [];
	const { grants, denies } = parseOverrides(override);
	const set = new Set<Permission>(defaults);
	for (const g of grants) set.add(g);
	for (const d of denies) set.delete(d);
	// Stable order for snapshot tests + diff stability in UI
	return PERMISSIONS.filter((p) => set.has(p));
}

export function hasPermission(
	effective: ReadonlyArray<string>,
	required: Permission,
): boolean {
	return effective.includes(required);
}

// ===== API shapes =====

export interface MemberPermissionsApi {
	member_id: string;
	role: ClinicRole;
	full_name: string;
	effective: Permission[];
	override: PermissionsOverride;
	defaults: Permission[];
}

export const updatePermissionsInputSchema = z.object({
	override: permissionsOverrideSchema,
});
export type UpdatePermissionsInput = z.infer<
	typeof updatePermissionsInputSchema
>;

/** Returned by `GET /auth/permissions` — what the CURRENT user can do. */
export interface CurrentUserPermissions {
	role: ClinicRole;
	effective: Permission[];
}
